import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { doc, onSnapshot, updateDoc, query, collection, orderBy, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useDrawAdmin } from './DrawAdminContext';
import { performRandomDraw } from '../utils/drawUtils';
import { distributeRewards } from '../utils/rewardUtils';

interface DrawHistory {
  drawId: string;
  date: Date;
  totalParticipants: number;
  totalAmount: number;
  winners: Winner[];
}

interface DrawContextType {
  drawTime: Date | null;
  setDrawTime: (time: Date | null) => Promise<void>;
  startDraw: (numberOfWinners: number) => Promise<void>;
  isDrawing: boolean;
  isAdmin: boolean;
  currentDraw: {
    status: 'pending' | 'in_progress' | 'completed';
    totalAmount: number;
    winners: Winner[];
  };
  drawHistory: DrawHistory[];
}

const DrawContext = createContext<DrawContextType>({
  drawTime: null,
  setDrawTime: async () => {},
  startDraw: async () => {},
  isDrawing: false,
  isAdmin: false,
  currentDraw: {
    status: 'pending',
    totalAmount: 0,
    winners: []
  },
  drawHistory: []
});

interface Winner {
  userId: string;
  amount: number;
  claimed: boolean;
}

export function DrawProvider({ children }: { children: ReactNode }) {
  const { isAdmin } = useDrawAdmin();
  const [drawTime, setDrawTimeState] = useState<Date | null>(null);
  const [currentDraw, setCurrentDraw] = useState<{
    status: 'pending' | 'in_progress' | 'completed';
    totalAmount: number;
    winners: any[];
  }>({
    status: 'pending',
    totalAmount: 0,
    winners: []
  });
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawHistory, setDrawHistory] = useState<DrawHistory[]>([]);

  // Écouter les changements du tirage en cours
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'draws', 'current'), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setCurrentDraw({
          status: data.status,
          totalAmount: data.totalAmount,
          winners: data.winners || []
        });
        setDrawTimeState(data.drawTime?.toDate() || null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Charger l'historique des tirages
  useEffect(() => {
    const q = query(collection(db, 'drawHistory'), orderBy('date', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const history = snapshot.docs.map(doc => ({
        ...doc.data(),
        drawId: doc.id,
        date: doc.data().date.toDate()
      })) as DrawHistory[];
      setDrawHistory(history);
    });

    return () => unsubscribe();
  }, []);

  const setDrawTime = async (time: Date | null) => {
    if (!time) return;
    
    try {
      const drawRef = doc(db, 'draws', 'current');
      await setDoc(drawRef, {
        drawTime: time,
        status: 'pending'
      }, { merge: true });
    } catch (error) {
      console.error('Erreur lors de la définition de l\'heure du tirage:', error);
    }
  };

  const startDraw = async (numberOfWinners: number) => {
    try {
      setIsDrawing(true);
      
      await updateDoc(doc(db, 'draws', 'current'), {
        status: 'in_progress'
      });

      const winners = await performRandomDraw(numberOfWinners, currentDraw.totalAmount);
      const winnersWithClaim: Winner[] = winners.map(w => ({
        ...w,
        claimed: false
      }));
      
      await distributeRewards('current', winnersWithClaim, currentDraw.totalAmount);
      
    } catch (error) {
      console.error('Erreur lors du tirage:', error);
      throw error;
    } finally {
      setIsDrawing(false);
    }
  };

  return (
    <DrawContext.Provider value={{
      drawTime,
      setDrawTime,
      currentDraw,
      startDraw,
      isDrawing,
      isAdmin,
      drawHistory
    }}>
      {children}
    </DrawContext.Provider>
  );
}

export const useDraw = () => {
  const context = useContext(DrawContext);
  if (!context) {
    throw new Error('useDraw must be used within DrawProvider');
  }
  return context;
};